"use client";

// NOTE: Do not change the markup/classnames inside <main className="detailed-comparison-main">...</main>

import { useState } from "react";
import {
  Download,
  Save,
  RefreshCw,
  Users,
  Award,
  Target,
  TrendingUp,
  FileText,
  Briefcase,
  BookOpen,
  Shield,
  Activity,
} from "lucide-react";
import "./user-user-detailed.css";
import BackButton from "../../shared/backButton";
import Modal from "../../layout/Modal";

const readinessCategory = (score) => {
  if (score >= 90) return "Promotion-ready";
  if (score >= 80) return "Leadership-ready";
  if (score >= 70) return "Near-ready";
  return "Not-ready";
};

const clampPct = (v) => Math.max(0, Math.min(100, Number.isFinite(v) ? v : 0));

const UserDetailedReport = ({ comparisonData }) => {
  console.log(comparisonData, "comparisonData from <UserTeamDetailedReport />");

  const [activeSection, setActiveSection] = useState("overview");
  const [activeSubTab, setActiveSubTab] = useState("analysis");
  const [activeSkillTab, setActiveSkillTab] = useState("soft");
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showFilenameModal, setShowFilenameModal] = useState(false);
  const [filename, setFilename] = useState("");

  const userA = {
    name: `${comparisonData.detailedReport.userDetails.userA.name}`,
    role: `${comparisonData.detailedReport.userDetails.userA.designation}`,
    department: `${comparisonData.detailedReport.userDetails.userA.department}`,
    // team: `${comparisonData.detailedReport.userDetails.userA.team}`,
    performanceScore: `${comparisonData.detailedReport.userDetails.userA.performanceScore}`,
    education: `${comparisonData.detailedReport.userDetails.userA.education}`,
    experienceYears: `${comparisonData.detailedReport.userDetails.userA.experience}`,
    leadershipScore: `${comparisonData.detailedReport.overview.userA.leadershipScore}`,
    strengths: ["Adaptability"],
    weaknesses: ["Innovation"],
    missingSoftSkills: ["Influence", "Storytelling"],
    missingJobSkills: ["Machine Learning", "Cloud Computing"],
    mobilityScore: 82,
    topRoles: [
      {
        role: "Data Analyst",
        mobilityScore: 82,
        strengths: [
          "SQL",
          "Data Visualization",
          "Aptitude: Analytical Reasoning",
          "Collaboration",
        ],
        weaknesses: ["Cloud Computing"],
        degree_gap: [],
        major_gap: [],
        job_experience_gap: 0,
      },
      {
        role: "Business Intelligence Specialist",
        mobilityScore: 82,
        strengths: ["SQL", "Dashboarding", "Aptitude: Pattern Recognition"],
        weaknesses: ["Data Storytelling"],
        degree_gap: [],
        major_gap: [],
        job_experience_gap: 1,
      },
    ],
  };

  const userB = {
    name: `${comparisonData.detailedReport.userDetails.userB.name}`,
    role: `${comparisonData.detailedReport.userDetails.userB.designation}`,
    department: `${comparisonData.detailedReport.userDetails.userB.department}`,
    // team: `${comparisonData.detailedReport.userDetails.userB.team}`,
    performanceScore: `${comparisonData.detailedReport.userDetails.userB.performanceScore}`,
    education: `${comparisonData.detailedReport.userDetails.userB.education}`,
    experienceYears: `${comparisonData.detailedReport.userDetails.userB.experienceYears}`,
    leadershipScore: `${comparisonData.detailedReport.overview.userB.leadershipScore}`,
    strengths: ["Innovation"],
    weaknesses: ["Adaptability"],
    missingSoftSkills: ["Adaptability"],
    missingJobSkills: ["SQL", "Data Visualization"],
    mobilityScore: 74,
    topRoles: [
      {
        role: "Data Engineer",
        mobilityScore: 74,
        strengths: ["Python", "ETL", "Aptitude: Problem Solving"],
        weaknesses: ["Data Visualization", "Stakeholder Communication"],
        degree_gap: ["Bachelor's Degree"],
        major_gap: ["Computer Science"],
        job_experience_gap: 2,
      },
    ],
  };

  const title = "User vs User Detailed Comparison Report";

  const sections = [
    { id: "overview", label: "Overview", icon: Activity },
    { id: "userDetails", label: "User Details", icon: Users },
    { id: "leadership", label: "Leadership Potential", icon: Award },
    { id: "skillGaps", label: "Skill Gaps", icon: Target },
    { id: "mobility", label: "Mobility Readiness", icon: TrendingUp },
    { id: "summary", label: "Summary", icon: FileText },
  ];

  const handleDownloadPDF = () => {
    const element = document.createElement("a");
    const file = new Blob(
      [
        `User vs User Detailed Report\n\nGenerated on: ${new Date().toLocaleDateString()}\n\nUsers: ${
          userA.name
        } vs ${userB.name}`,
      ],
      { type: "text/plain" }
    );
    element.href = URL.createObjectURL(file);
    element.download = `user-vs-user-report-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const generateDefaultFilename = () => {
    const date = new Date().toISOString().split("T")[0];
    return `user-vs-user-report-${date}`;
  };

  const handleSaveReport = () => {
    const defaultName = generateDefaultFilename();
    setFilename(defaultName);
    setShowFilenameModal(true);
  };

  const confirmSaveReport = () => {
    if (!filename.trim()) return;
    setShowFilenameModal(false);
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setShowSaveModal(true);
    }, 1200);
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setActiveSubTab("analysis");
    setActiveSkillTab("soft");
  };

  const metricCards = [
    {
      title: `Leadership Ready Score`,
      value: `${comparisonData.detailedReport.overview.userA.leadershipScore}%`,
      sub: `${
        comparisonData.detailedReport.overview.userA.name.split(" ")[0]
      } - ${readinessCategory(
        comparisonData.detailedReport.overview.userA.leadershipScore
      )}`,
      variant: "primary",
      icon: Award,
    },
    {
      title: `Leadership Ready Score`,
      value: `${comparisonData.detailedReport.overview.userB.leadershipScore}%`,
      sub: `${
        comparisonData.detailedReport.overview.userB.name.split(" ")[0]
      } - ${readinessCategory(
        comparisonData.detailedReport.overview.userB.leadershipScore
      )}`,
      variant: "secondary",
      icon: Award,
    },
    {
      title: `Strengths Count`,
      value: String(
        comparisonData.detailedReport.overview.userA.strengths.length
      ),
      sub: `${comparisonData.detailedReport.overview.userA.name.split(" ")[0]}`,
      variant: "accent",
      icon: Shield,
    },
    {
      title: `Strengths Count`,
      value: String(
        comparisonData.detailedReport.overview.userB.strengths.length
      ),
      sub: `${comparisonData.detailedReport.overview.userB.name.split(" ")[0]}`,
      variant: "success",
      icon: Shield,
    },
    {
      title: `Weak Skills Count`,
      value: String(
        comparisonData.detailedReport.overview.userA.weaknesses.length
      ),
      sub: `${comparisonData.detailedReport.overview.userA.name.split(" ")[0]}`,
      variant: "warning",
      icon: Target,
    },
    {
      title: `Weak Skills Count`,
      value: String(
        comparisonData.detailedReport.overview.userB.weaknesses.length
      ),
      sub: `${comparisonData.detailedReport.overview.userB.name.split(" ")[0]}`,
      variant: "danger",
      icon: Target,
    },
    {
      title: `Missing Soft Skills Count`,
      value: String(
        comparisonData.detailedReport.overview.userA.missingSoftSkills.length
      ),
      sub: `${comparisonData.detailedReport.overview.userA.name.split(" ")[0]}`,
      variant: "info",
      icon: BookOpen,
    },
    {
      title: `Missing Soft Skills Count`,
      value: String(
        comparisonData.detailedReport.overview.userB.missingSoftSkills.length
      ),
      sub: `${comparisonData.detailedReport.overview.userB.name.split(" ")[0]}`,
      variant: "info",
      icon: BookOpen,
    },
    {
      title: `Missing Job Skills Count`,
      value: String(
        comparisonData.detailedReport.overview.userA.missingJobSkills.length
      ),
      sub: `${comparisonData.detailedReport.overview.userA.name.split(" ")[0]}`,
      variant: "neutral",
      icon: Briefcase,
    },
    {
      title: `Missing Job Skills Count`,
      value: String(
        comparisonData.detailedReport.overview.userB.missingJobSkills.length
      ),
      sub: `${comparisonData.detailedReport.overview.userB.name.split(" ")[0]}`,
      variant: "neutral",
      icon: Briefcase,
    },
    {
      title: `Mobility Score`,
      value: `${comparisonData.detailedReport.overview.userA.mobilityScore}%`,
      sub: `${comparisonData.detailedReport.overview.userA.name.split(" ")[0]}`,
      variant: "primary",
      icon: TrendingUp,
    },
    {
      title: `Mobility Score`,
      value: `${comparisonData.detailedReport.overview.userB.mobilityScore}%`,
      sub: `${comparisonData.detailedReport.overview.userB.name.split(" ")[0]}`,
      variant: "secondary",
      icon: TrendingUp,
    },
  ];

  const renderSubTabs = (id) => {
    if (["leadership", "skillGaps", "mobility"].includes(id)) {
      return (
        <div className="user-detailed-sub-tabs">
          <button
            className={`user-detailed-sub-tab ${
              activeSubTab === "analysis" ? "active" : ""
            }`}
            onClick={() => setActiveSubTab("analysis")}
          >
            Analysis
          </button>
          <button
            className={`user-detailed-sub-tab ${
              activeSubTab === "visualization" ? "active" : ""
            }`}
            onClick={() => setActiveSubTab("visualization")}
          >
            Visualization
          </button>
        </div>
      );
    }
    return null;
  };

  const renderSkillTabs = () => (
    <div className="user-detailed-skill-tabs">
      <button
        className={`user-detailed-skill-tab ${
          activeSkillTab === "soft" ? "active" : ""
        }`}
        onClick={() => setActiveSkillTab("soft")}
      >
        Soft Skills
      </button>
      <button
        className={`user-detailed-skill-tab ${
          activeSkillTab === "job" ? "active" : ""
        }`}
        onClick={() => setActiveSkillTab("job")}
      >
        Job Skills
      </button>
    </div>
  );

  const ProgressBar = ({ value, colorClass, label }) => (
    <div className="user-detailed-progress-row">
      {label ? (
        <span className="user-detailed-progress-label">{label}</span>
      ) : null}
      <div className="user-detailed-progress-bar">
        <div
          className={`user-detailed-progress-fill ${colorClass}`}
          style={{ width: `${clampPct(value)}%` }}
        />
      </div>
      <span className="user-detailed-progress-score">
        {clampPct(value).toFixed(0)}%
      </span>
    </div>
  );

  const Pill = ({ children, tone = "default" }) => (
    <span className={`user-detailed-pill user-detailed-pill-${tone}`}>
      {children}
    </span>
  );

  const RoleCard = ({ role, score, strengths, weaknesses, gaps }) => (
    <div className="user-detailed-role-card">
      <div className="user-detailed-role-header">
        <h5>{role}</h5>
        <div className="user-detailed-score-badge">{score}%</div>
      </div>
      <div className="user-detailed-role-body">
        <div className="user-detailed-role-section">
          <strong>Strengths</strong>
          <div className="user-detailed-chip-row">
            {strengths.slice(0, 4).map((s, i) => (
              <Pill key={i} tone="success">
                {s}
              </Pill>
            ))}
          </div>
        </div>
        {weaknesses?.length ? (
          <div className="user-detailed-role-section">
            <strong>Weaknesses</strong>
            <div className="user-detailed-chip-row">
              {weaknesses.slice(0, 4).map((w, i) => (
                <Pill key={i} tone="warning">
                  {w}
                </Pill>
              ))}
            </div>
          </div>
        ) : null}
        {gaps &&
        (gaps.degree_gap?.length ||
          gaps.major_gap?.length ||
          gaps.job_experience_gap > 0) ? (
          <div className="user-detailed-role-section">
            <strong>Gaps</strong>
            <div className="user-detailed-gap-grid">
              <div className="user-detailed-gap-item">
                <span className="user-detailed-gap-label">Degree</span>
                <span className="user-detailed-gap-value">
                  {(gaps.degree_gap || []).join(", ") || "None"}
                </span>
              </div>
              <div className="user-detailed-gap-item">
                <span className="user-detailed-gap-label">Major</span>
                <span className="user-detailed-gap-value">
                  {(gaps.major_gap || []).join(", ") || "None"}
                </span>
              </div>
              <div className="user-detailed-gap-item">
                <span className="user-detailed-gap-label">Experience</span>
                <span className="user-detailed-gap-value">
                  {gaps.job_experience_gap
                    ? `${gaps.job_experience_gap} yr gap`
                    : "No gap"}
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="user-detailed-section-content">
            <div className="user-detailed-overview-grid">
              {metricCards.map((m, idx) => {
                const Icon = m.icon;
                return (
                  <div
                    key={idx}
                    className={`user-detailed-overview-card user-detailed-${m.variant}`}
                  >
                    <div className="user-detailed-overview-icon">
                      <Icon size={22} />
                    </div>
                    <div className="user-detailed-overview-content">
                      <h3>{m.title}</h3>
                      <div className="user-detailed-score-display">
                        {m.value}
                      </div>
                      <p>{m.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "userDetails":
        return (
          <div className="user-detailed-section-content">
            <h3 style={{ marginBottom: 10 }}>User Details</h3>
            <p style={{ marginBottom: 20 }}>
              Profile snapshots for each user: core attributes and recent
              performance indicators.
            </p>
            <div className="user-detailed-user-grid">
              {[userA, userB].map((u, i) => (
                <div className="user-detailed-user-card" key={i}>
                  <div className="user-detailed-user-header">
                    <Users size={18} />
                    <h4>{u.name}</h4>
                  </div>
                  <div className="user-detailed-info-list">
                    <div className="user-detailed-info-row">
                      <span className="user-detailed-info-label">Role</span>
                      <span className="user-detailed-info-value">{u.role}</span>
                    </div>
                    <div className="user-detailed-info-row">
                      <span className="user-detailed-info-label">
                        Department
                      </span>
                      <span className="user-detailed-info-value">
                        {u.department}
                      </span>
                    </div>
                    <div className="user-detailed-info-row">
                      {/* <span className="user-detailed-info-label">
                        Team Assigned
                      </span> */}
                      {/* <span className="user-detailed-info-value">
                        {u.team || "Not assigned"}
                      </span> */}
                    </div>
                    <div className="user-detailed-info-row">
                      <span className="user-detailed-info-label">
                        Performance Score
                      </span>
                      <span className="user-detailed-info-value">
                        {u.performanceScore}%
                      </span>
                    </div>
                    <div className="user-detailed-info-row">
                      <span className="user-detailed-info-label">
                        Education
                      </span>
                      <span className="user-detailed-info-value">
                        {u.education}
                      </span>
                    </div>
                    <div className="user-detailed-info-row">
                      <span className="user-detailed-info-label">
                        Experience
                      </span>
                      <span className="user-detailed-info-value">
                        {u.experienceYears} years
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "leadership":
        return (
          <div className="user-detailed-section-content">
            {renderSubTabs("leadership")}
            {activeSubTab === "analysis" ? (
              <div className="user-detailed-analysis-container">
                <h3>Leadership Potential Analysis</h3>
                <div className="user-detailed-analysis-content">
                  <p>
                    {comparisonData.detailedReport.leadershipMetrics.analysis}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: 10 }}>
                  Leadership Readiness Comparison
                </h3>
                <p style={{ marginBottom: 20 }}>
                  Comparative leadership readiness and core trait profile.
                </p>
                <div
                  className="dept-detailed-mobility-card"
                  style={{ marginBottom: "20px" }}
                >
                  <h4>Leadership Readiness Score</h4>
                  <div className="dept-detailed-comparison-bars">
                    {/* Dept A */}
                    <div className="dept-detailed-department-bar">
                      <span className="dept-detailed-department-label">
                        {
                          comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[0].name
                        }
                      </span>
                      <div className="dept-detailed-progress-container">
                        <div className="dept-detailed-progress-bar">
                          <div
                            className="dept-detailed-progress-fill dept-detailed-dept1"
                            style={{
                              width: `${Math.max(
                                0,
                                Math.min(
                                  100,
                                  Number(
                                    comparisonData.detailedReport
                                      .leadershipMetrics.visualization.score[0]
                                      .leadershipScore
                                  )
                                )
                              )}%`,
                            }}
                          />
                        </div>
                        <span className="dept-detailed-score">
                          {Number.isFinite(
                            Number(
                              readinessCategory(
                                comparisonData.detailedReport.leadershipMetrics
                                  .visualization.score[0].readinessCategory
                              )
                            )
                          )
                            ? `${Number(
                                readinessCategory(
                                  comparisonData.detailedReport
                                    .leadershipMetrics.visualization.score[0]
                                    .readinessCategory
                                )
                              ).toFixed(2)}%`
                            : comparisonData.detailedReport.leadershipMetrics
                                .visualization.score[0].leadershipScore}
                          %
                        </span>
                      </div>
                    </div>

                    {/* Dept B */}
                    <div className="dept-detailed-department-bar">
                      <span className="dept-detailed-department-label">
                        {
                          comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[1].name
                        }
                      </span>
                      <div className="dept-detailed-progress-container">
                        <div className="dept-detailed-progress-bar">
                          <div
                            className="dept-detailed-progress-fill dept-detailed-dept2"
                            style={{
                              width: `${Math.max(
                                0,
                                Math.min(
                                  100,
                                  Number(
                                    comparisonData.detailedReport
                                      .leadershipMetrics.visualization.score[1]
                                      .leadershipScore
                                  )
                                )
                              )}%`,
                            }}
                          />
                        </div>
                        <span className="dept-detailed-score">
                          {Number.isFinite(
                            Number(
                              comparisonData.detailedReport.leadershipMetrics
                                .visualization.score[1].readinessCategory
                            )
                          )
                            ? `${Number(
                                comparisonData.detailedReport.leadershipMetrics
                                  .visualization.score[1].readinessCategory
                              ).toFixed(2)}%`
                            : comparisonData.detailedReport.leadershipMetrics
                                .visualization.score[1].leadershipScore}
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="user-detailed-compare-grid">
                  <div className="user-detailed-compare-card">
                    <div className="user-detailed-compare-header">
                      <Award size={18} />
                      <h4>
                        {
                          comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[0].name
                        }
                      </h4>
                    </div>
                    <div className="user-detailed-readiness-row">
                      <span className="user-detailed-readiness-chip">
                        {readinessCategory(
                          comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[0].readinessCategory
                        )}
                      </span>
                    </div>
                    <div className="user-detailed-dual-columns">
                      <div>
                        <h5>Strengths</h5>
                        <div className="user-detailed-chip-row">
                          {comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[0].strengths.length ? (
                            comparisonData.detailedReport.leadershipMetrics.visualization.score[0].strengths.map(
                              (s, i) => (
                                <Pill key={i} tone="success">
                                  {s}
                                </Pill>
                              )
                            )
                          ) : (
                            <span>No strengths</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <h5>Weaknesses</h5>
                        <div className="user-detailed-chip-row">
                          {comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[0].weaknesses.length ? (
                            comparisonData.detailedReport.leadershipMetrics.visualization.score[0].weaknesses.map(
                              (w, i) => (
                                <Pill key={i} tone="warning">
                                  {w}
                                </Pill>
                              )
                            )
                          ) : (
                            <span>No weaknesses</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="user-detailed-compare-card">
                    <div className="user-detailed-compare-header">
                      <Award size={18} />
                      <h4>
                        {
                          comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[1].name
                        }
                      </h4>
                    </div>
                    <div className="user-detailed-readiness-row">
                      <span className="user-detailed-readiness-chip">
                        {readinessCategory(
                          comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[1].readinessCategory
                        )}
                      </span>
                    </div>
                    <div className="user-detailed-dual-columns">
                      <div>
                        <h5>Strengths</h5>
                        <div className="user-detailed-chip-row">
                          {comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[1].strengths.length ? (
                            comparisonData.detailedReport.leadershipMetrics.visualization.score[1].strengths.map(
                              (s, i) => (
                                <Pill key={i} tone="success">
                                  {s}
                                </Pill>
                              )
                            )
                          ) : (
                            <span>No strengths</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <h5>Weaknesses</h5>
                        <div className="user-detailed-chip-row">
                          {comparisonData.detailedReport.leadershipMetrics
                            .visualization.score[1].weaknesses.length ? (
                            comparisonData.detailedReport.leadershipMetrics.visualization.score[1].weaknesses.map(
                              (w, i) => (
                                <Pill key={i} tone="warning">
                                  {w}
                                </Pill>
                              )
                            )
                          ) : (
                            <span>No weaknesses</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "skillGaps":
        return (
          <div className="user-detailed-section-content">
            {renderSubTabs("skillGaps")}
            {activeSubTab === "analysis" ? (
              <div className="user-detailed-analysis-container">
                <h3>Skill Gap Delta Analysis</h3>
                <div className="user-detailed-analysis-content">
                  <p>
                    {comparisonData.detailedReport.skillGapsMetrics.analysis}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: 10 }}>Skill Gap Delta</h3>
                <p style={{ marginBottom: 20 }}>
                  Soft and job skill gaps across both users.
                </p>
                {renderSkillTabs()}

                {activeSkillTab === "soft" ? (
                  <div className="user-detailed-skill-grid">
                    <div className="user-detailed-skill-card">
                      <div className="user-detailed-skill-header">
                        <h4 style={{ fontWeight: "500" }}>
                          {
                            comparisonData.detailedReport.skillGapsMetrics
                              .visualization.softSkills[0].name
                          }{" "}
                          — Soft Skills
                        </h4>
                        <span className="user-detailed-count-badge">
                          {
                            comparisonData.detailedReport.skillGapsMetrics
                              .visualization.softSkills[0].missingSoftSkills
                              .length
                          }{" "}
                          missing
                        </span>
                      </div>
                      <div className="user-detailed-chip-row">
                        {comparisonData.detailedReport.skillGapsMetrics
                          .visualization.softSkills[0].missingSoftSkills
                          .length ? (
                          comparisonData.detailedReport.skillGapsMetrics.visualization.softSkills[0].missingSoftSkills.map(
                            (s, i) => (
                              <Pill key={i} tone="warning">
                                {s}
                              </Pill>
                            )
                          )
                        ) : (
                          <span>No missing soft skills</span>
                        )}
                      </div>
                    </div>

                    <div className="user-detailed-skill-card">
                      <div className="user-detailed-skill-header">
                        <h4 style={{ fontWeight: "500" }}>
                          {
                            comparisonData.detailedReport.skillGapsMetrics
                              .visualization.softSkills[1].name
                          }{" "}
                          — Soft Skills
                        </h4>
                        <span className="user-detailed-count-badge">
                          {
                            comparisonData.detailedReport.skillGapsMetrics
                              .visualization.softSkills[1].missingSoftSkills
                              .length
                          }{" "}
                          missing
                        </span>
                      </div>
                      <div className="user-detailed-chip-row">
                        {comparisonData.detailedReport.skillGapsMetrics
                          .visualization.softSkills[1].missingSoftSkills
                          .length ? (
                          comparisonData.detailedReport.skillGapsMetrics.visualization.softSkills[1].missingSoftSkills.map(
                            (s, i) => (
                              <Pill key={i} tone="warning">
                                {s}
                              </Pill>
                            )
                          )
                        ) : (
                          <span>No missing soft skills</span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="user-detailed-skill-grid">
                    <div className="user-detailed-skill-card">
                      <div className="user-detailed-skill-header">
                        <h4>
                          {
                            comparisonData.detailedReport.skillGapsMetrics
                              .visualization.jobSkills[0].name
                          }{" "}
                          — Job Skills
                        </h4>
                        <span className="user-detailed-count-badge">
                          {
                            comparisonData.detailedReport.skillGapsMetrics
                              .visualization.jobSkills[0].missingJobSkills
                              .length
                          }{" "}
                          missing
                        </span>
                      </div>
                      <div className="user-detailed-chip-row">
                        {comparisonData.detailedReport.skillGapsMetrics
                          .visualization.jobSkills[0].missingJobSkills
                          .length ? (
                          comparisonData.detailedReport.skillGapsMetrics.visualization.jobSkills[0].missingJobSkills.map(
                            (s, i) => (
                              <Pill key={i} tone="danger">
                                {s}
                              </Pill>
                            )
                          )
                        ) : (
                          <span>No missing job skills</span>
                        )}
                        {/* {userA.missingJobSkills.map((s, i) => (
                          <Pill key={i} tone="danger">
                            {s}
                          </Pill>
                        ))} */}
                      </div>
                    </div>

                    <div className="user-detailed-skill-card">
                      <div className="user-detailed-skill-header">
                        <h4>
                          {
                            comparisonData.detailedReport.skillGapsMetrics
                              .visualization.jobSkills[1].name
                          }{" "}
                          — Job Skills
                        </h4>
                        <span className="user-detailed-count-badge">
                          {
                            comparisonData.detailedReport.skillGapsMetrics
                              .visualization.jobSkills[1].missingJobSkills
                              .length
                          }{" "}
                          missing
                        </span>
                      </div>
                      <div className="user-detailed-chip-row">
                        {comparisonData.detailedReport.skillGapsMetrics
                          .visualization.jobSkills[1].missingJobSkills
                          .length ? (
                          comparisonData.detailedReport.skillGapsMetrics.visualization.jobSkills[1].missingJobSkills.map(
                            (s, i) => (
                              <Pill key={i} tone="danger">
                                {s}
                              </Pill>
                            )
                          )
                        ) : (
                          <span>No missing job skills</span>
                        )}
                        {/* {userB.missingJobSkills.map((s, i) => (
                          <Pill key={i} tone="danger">
                            {s}
                          </Pill>
                        ))} */}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case "mobility":
        return (
          <div className="user-detailed-section-content">
            {renderSubTabs("mobility")}
            {activeSubTab === "analysis" ? (
              <div className="user-detailed-analysis-container">
                <h3>Mobility Readiness Analysis</h3>
                <div className="user-detailed-analysis-content">
                  <p>
                    {comparisonData.detailedReport.mobilityMetrics.analysis}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: 10 }}>Mobility Readiness</h3>
                <p style={{ marginBottom: 20 }}>
                  Readiness comparison, suggested roles, comparative strengths
                  and weaknesses, and qualification gaps.
                </p>

                <div className="mobility-viz-container">
                  {/* Overall Readiness Comparison */}
                  <div
                    className="dept-detailed-mobility-card"
                    style={{ marginBottom: "20px" }}
                  >
                    <h4>Average Mobility Score</h4>
                    <div className="dept-detailed-comparison-bars">
                      {/* Dept A */}
                      <div className="dept-detailed-department-bar">
                        <span className="dept-detailed-department-label">
                          {
                            comparisonData.detailedReport.mobilityMetrics
                              .visualization.userA.name
                          }
                        </span>
                        <div className="dept-detailed-progress-container">
                          <div className="dept-detailed-progress-bar">
                            <div
                              className="dept-detailed-progress-fill dept-detailed-dept1"
                              style={{
                                width: `${Math.max(
                                  0,
                                  Math.min(
                                    100,
                                    Number(
                                      comparisonData.detailedReport
                                        .mobilityMetrics.visualization.userA
                                        .roleRecommendations[0]
                                        .mobility_score ?? 0
                                    )
                                  )
                                )}%`,
                              }}
                            />
                          </div>
                          <span className="dept-detailed-score">
                            {Number.isFinite(
                              Number(
                                readinessCategory(
                                  comparisonData.detailedReport.mobilityMetrics
                                    .visualization.userA.roleRecommendations[0]
                                    .mobility_score
                                )
                              )
                            )
                              ? `${Number(
                                  readinessCategory(
                                    comparisonData.detailedReport
                                      .mobilityMetrics.visualization.userA
                                      .roleRecommendations[0].mobility_score
                                  )
                                ).toFixed(2)}%`
                              : comparisonData.detailedReport.mobilityMetrics
                                  .visualization.userA.roleRecommendations[0]
                                  .mobility_score}
                            %
                          </span>
                        </div>
                      </div>

                      {/* Dept B */}
                      <div className="dept-detailed-department-bar">
                        <span className="dept-detailed-department-label">
                          {
                            comparisonData.detailedReport.mobilityMetrics
                              .visualization.userB.name
                          }
                        </span>
                        <div className="dept-detailed-progress-container">
                          <div className="dept-detailed-progress-bar">
                            <div
                              className="dept-detailed-progress-fill dept-detailed-dept2"
                              style={{
                                width: `${Math.max(
                                  0,
                                  Math.min(
                                    100,
                                    Number(
                                      comparisonData.detailedReport
                                        .mobilityMetrics.visualization.userB
                                        .roleRecommendations[0]
                                        .mobility_score ?? 0
                                    )
                                  )
                                )}%`,
                              }}
                            />
                          </div>
                          <span className="dept-detailed-score">
                            {Number.isFinite(
                              Number(
                                readinessCategory(
                                  comparisonData.detailedReport.mobilityMetrics
                                    .visualization.userB.roleRecommendations[0]
                                    .mobility_score
                                )
                              )
                            )
                              ? `${Number(
                                  readinessCategory(
                                    comparisonData.detailedReport
                                      .mobilityMetrics.visualization.userB
                                      .roleRecommendations[0].mobility_score
                                  )
                                ).toFixed(2)}%`
                              : comparisonData.detailedReport.mobilityMetrics
                                  .visualization.userB.roleRecommendations[0]
                                  .mobility_score}
                            %
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Role Recommendations Comparison */}
                  <div className="mobility-viz-recommendations">
                    <h3 style={{ marginBottom: 10 }}>Role Recommendations</h3>

                    <div className="mobility-viz-roles-comparison">
                      {/* User A Roles */}
                      <div className="mobility-viz-user-roles mobility-viz-user-a-roles">
                        <div className="mobility-viz-user-header">
                          <span className="mobility-viz-user-badge mobility-viz-badge-a">
                            {
                              comparisonData.detailedReport.mobilityMetrics
                                .visualization.userA.name
                            }
                          </span>
                          <span className="mobility-viz-role-count">
                            {
                              comparisonData.detailedReport.mobilityMetrics
                                .visualization.userA.roleRecommendations.length
                            }{" "}
                            Recommendations
                          </span>
                        </div>

                        {comparisonData.detailedReport.mobilityMetrics.visualization.userA.roleRecommendations.map(
                          (role, idx) => (
                            // {userA.topRoles.map((role, idx) => (
                            <div
                              key={idx}
                              className="mobility-viz-role-recommendation"
                            >
                              <div className="mobility-viz-role-header">
                                <div className="mobility-viz-role-transition">
                                  <span className="mobility-viz-current-role">
                                    {role.current_role}
                                  </span>
                                  <div className="mobility-viz-arrow">→</div>
                                  <span className="mobility-viz-suggested-role">
                                    {role.role}
                                  </span>
                                </div>
                                <div className="mobility-viz-match-score">
                                  <span className="mobility-viz-match-value">
                                    {role.mobility_score}%
                                  </span>
                                  <span className="mobility-viz-match-label">
                                    Match
                                  </span>
                                </div>
                              </div>

                              <div className="mobility-viz-role-details">
                                <div className="mobility-viz-strengths-section">
                                  <h6>Strengths</h6>
                                  <div className="mobility-viz-skills-grid">
                                    {role.strengths.map((strength, i) => (
                                      <span
                                        key={i}
                                        className="mobility-viz-skill-tag mobility-viz-strength"
                                      >
                                        {strength}
                                      </span>
                                    ))}
                                  </div>
                                </div>

                                {role.weaknesses &&
                                  role.weaknesses.length > 0 && (
                                    <div className="mobility-viz-weaknesses-section">
                                      <h6>Areas to Develop</h6>
                                      <div className="mobility-viz-skills-grid">
                                        {role.weaknesses.map((weakness, i) => (
                                          <span
                                            key={i}
                                            className="mobility-viz-skill-tag mobility-viz-weakness"
                                          >
                                            {weakness}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                <div className="mobility-viz-gaps-section">
                                  <h6>Qualification Gaps</h6>
                                  <div className="mobility-viz-gaps-grid">
                                    <div className="mobility-viz-gap-item">
                                      <span className="mobility-viz-gap-type">
                                        Degree
                                      </span>
                                      <span className="mobility-viz-gap-status">
                                        {role.degree_gap &&
                                        role.degree_gap.length > 0
                                          ? role.degree_gap.join(", ")
                                          : "✓ Met"}
                                      </span>
                                    </div>
                                    <div className="mobility-viz-gap-item">
                                      <span className="mobility-viz-gap-type">
                                        Major
                                      </span>
                                      <span className="mobility-viz-gap-status">
                                        {role.major_gap &&
                                        role.major_gap.length > 0
                                          ? role.major_gap.join(", ")
                                          : "✓ Met"}
                                      </span>
                                    </div>
                                    <div className="mobility-viz-gap-item">
                                      <span className="mobility-viz-gap-type">
                                        Experience
                                      </span>
                                      <span className="mobility-viz-gap-status">
                                        {role.job_experience_gap > 0
                                          ? `${role.job_experience_gap} yr gap`
                                          : "✓ Met"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>

                      {/* User B Roles */}
                      <div className="mobility-viz-user-roles mobility-viz-user-b-roles">
                        <div className="mobility-viz-user-header">
                          <span className="mobility-viz-user-badge mobility-viz-badge-b">
                            {
                              comparisonData.detailedReport.mobilityMetrics
                                .visualization.userB.name
                            }
                          </span>
                          <span className="mobility-viz-role-count">
                            {
                              comparisonData.detailedReport.mobilityMetrics
                                .visualization.userB.roleRecommendations.length
                            }{" "}
                            Recommendations
                          </span>
                        </div>
                        {comparisonData.detailedReport.mobilityMetrics.visualization.userB.roleRecommendations.map(
                          (role, idx) => (
                            // {userB.topRoles.map((role, idx) => (
                            <div
                              key={idx}
                              className="mobility-viz-role-recommendation"
                            >
                              <div className="mobility-viz-role-header">
                                <div className="mobility-viz-role-transition">
                                  <span className="mobility-viz-current-role">
                                    {role.current_role}
                                  </span>
                                  <div className="mobility-viz-arrow">→</div>
                                  <span className="mobility-viz-suggested-role">
                                    {role.role}
                                  </span>
                                </div>
                                <div className="mobility-viz-match-score">
                                  <span className="mobility-viz-match-value">
                                    {role.mobility_score}%
                                  </span>
                                  <span className="mobility-viz-match-label">
                                    Match
                                  </span>
                                </div>
                              </div>

                              <div className="mobility-viz-role-details">
                                <div className="mobility-viz-strengths-section">
                                  <h6>Strengths</h6>
                                  <div className="mobility-viz-skills-grid">
                                    {role.strengths.map((strength, i) => (
                                      <span
                                        key={i}
                                        className="mobility-viz-skill-tag mobility-viz-strength"
                                      >
                                        {strength}
                                      </span>
                                    ))}
                                  </div>
                                </div>

                                {role.weaknesses &&
                                  role.weaknesses.length > 0 && (
                                    <div className="mobility-viz-weaknesses-section">
                                      <h6>Areas to Develop</h6>
                                      <div className="mobility-viz-skills-grid">
                                        {role.weaknesses.map((weakness, i) => (
                                          <span
                                            key={i}
                                            className="mobility-viz-skill-tag mobility-viz-weakness"
                                          >
                                            {weakness}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                <div className="mobility-viz-gaps-section">
                                  <h6>Qualification Gaps</h6>
                                  <div className="mobility-viz-gaps-grid">
                                    <div className="mobility-viz-gap-item">
                                      <span className="mobility-viz-gap-type">
                                        Degree
                                      </span>
                                      <span className="mobility-viz-gap-status">
                                        {role.degree_gap &&
                                        role.degree_gap.length > 0
                                          ? role.degree_gap.join(", ")
                                          : "✓ Met"}
                                      </span>
                                    </div>
                                    <div className="mobility-viz-gap-item">
                                      <span className="mobility-viz-gap-type">
                                        Major
                                      </span>
                                      <span className="mobility-viz-gap-status">
                                        {role.major_gap &&
                                        role.major_gap.length > 0
                                          ? role.major_gap.join(", ")
                                          : "✓ Met"}
                                      </span>
                                    </div>
                                    <div className="mobility-viz-gap-item">
                                      <span className="mobility-viz-gap-type">
                                        Experience
                                      </span>
                                      <span className="mobility-viz-gap-status">
                                        {role.job_experience_gap > 0
                                          ? `${role.job_experience_gap} yr gap`
                                          : "✓ Met"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Skills Comparison Summary */}
                  <div className="mobility-viz-skills-summary">
                    <h4 className="mobility-viz-section-title">
                      Skills Comparison Overview
                    </h4>

                    <div className="mobility-viz-skills-comparison">
                      <div className="mobility-viz-skills-column">
                        <h5>Combined Strengths</h5>
                        <div className="mobility-viz-skills-cloud">
                          {Array.from(
                            new Set([
                              ...comparisonData.detailedReport.mobilityMetrics.visualization.userA.roleRecommendations.map(
                                (r) => r.strengths
                              ),
                              ...comparisonData.detailedReport.mobilityMetrics.visualization.userB.roleRecommendations.map(
                                (r) => r.strengths
                              ),
                            ])
                          )
                            .flat()
                            .map((skill, i) => (
                              <span
                                key={i}
                                className="mobility-viz-skill-bubble mobility-viz-strength-bubble"
                              >
                                {skill}
                              </span>
                            ))}
                        </div>
                      </div>

                      <div className="mobility-viz-skills-column">
                        <h5>Development Areas</h5>
                        <div className="mobility-viz-skills-cloud">
                          {Array.from(
                            new Set([
                              ...comparisonData.detailedReport.mobilityMetrics.visualization.userA.roleRecommendations.map(
                                (r) => r.weaknesses
                              ),
                              ...comparisonData.detailedReport.mobilityMetrics.visualization.userB.roleRecommendations.map(
                                (r) => r.weaknesses
                              ),
                            ])
                          )
                            .flat()
                            .map((skill, i) => (
                              <span
                                key={i}
                                className="mobility-viz-skill-bubble mobility-viz-weakness-bubble"
                              >
                                {skill}
                              </span>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "summary":
        return (
          <div className="user-detailed-section-content">
            <div className="user-detailed-summary-content">
              <div className="user-detailed-summary-section">
                <h4>Leadership Potential</h4>
                <p>
                  {comparisonData.detailedReport.leadershipMetrics.analysis}
                </p>
              </div>
              <div className="user-detailed-summary-section">
                <h4>Skill Gaps</h4>
                <p>{comparisonData.detailedReport.skillGapsMetrics.analysis}</p>
              </div>
              <div className="user-detailed-summary-section">
                <h4>Mobility Readiness</h4>
                <p>{comparisonData.detailedReport.mobilityMetrics.analysis}</p>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="user-detailed-section-content">Section not found</div>
        );
    }
  };

  return (
    <div className="user-detailed-comparison-page">
      <BackButton />

      {/* IMPORTANT: Do not change markup/classnames inside this main */}
      <main className="detailed-comparison-main">
        <div className="container">
          {/* Header */}
          <div className="detailed-comparison-header">
            <div className="detailed-header-content">
              <div className="detailed-header-text">
                <h1>{title}</h1>
                <p>Comprehensive comparative analysis by user</p>
              </div>
              {/*<div className="detailed-header-actions">
                <button className="detailed-action-btn detailed-secondary" onClick={handleDownloadPDF}>
                  <Download size={16} />
                  Download PDF
                </button>
                <button className="detailed-action-btn detailed-primary" onClick={handleSaveReport} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Report
                    </>
                  )}
                </button>
              </div>*/}
            </div>
          </div>

          <div className="user-detailed-comparison-layout">
            {/* Navigation Sidebar */}
            <div className="user-detailed-navigation">
              <div className="user-detailed-nav-header">
                <h3>Report Sections</h3>
              </div>
              <nav className="user-detailed-nav-menu">
                {sections.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.id}
                      className={`user-detailed-nav-item ${
                        activeSection === s.id ? "active" : ""
                      }`}
                      onClick={() => handleSectionChange(s.id)}
                    >
                      <Icon size={18} />
                      <span>{s.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="user-detailed-content">
              <div className="user-detailed-content-header">
                <h2>{sections.find((s) => s.id === activeSection)?.label}</h2>
              </div>
              {renderSection()}
            </div>
          </div>
        </div>
      </main>

      {/* Modals (Do not change modal component) */}
      {showFilenameModal && (
        <Modal
          show={showFilenameModal}
          onClose={() => setShowFilenameModal(false)}
          type="info"
          title="Save Report"
          showButton={false}
        >
          <div className="user-detailed-filename-modal-content">
            <div className="user-detailed-form-group">
              <label htmlFor="filename">Report Filename</label>
              <input
                type="text"
                id="filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="Enter filename..."
                className="user-detailed-filename-input"
                autoFocus
              />
            </div>
            <div className="user-detailed-modal-actions">
              <button
                className="user-detailed-modal-btn user-detailed-secondary"
                onClick={() => setShowFilenameModal(false)}
              >
                Cancel
              </button>
              <button
                className="user-detailed-modal-btn user-detailed-primary"
                onClick={confirmSaveReport}
                disabled={!filename.trim()}
              >
                Save Report
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showSaveModal && (
        <Modal
          show={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          type="success"
          title="Report Saved Successfully!"
          message={`Your user vs user report "${filename}" has been saved.`}
          showButton={true}
        />
      )}
    </div>
  );
};

export default UserDetailedReport;
